import { type MultiSelectDropdownItem } from './types'

export type CategoryHeaderControlValue = 'checked' | 'indeterminate' | 'unchecked'

export const getLeafOptions = (options: MultiSelectDropdownItem[]): MultiSelectDropdownItem[] => {
  return options.flatMap((option) => {
    if (option.subOptions != null) {
      return option.subOptions
    }
    return [option]
  })
}

export const getFullCategoryOptionsByLabel = (
  groups: Array<{ label: string; options: MultiSelectDropdownItem[] }> | undefined
): Map<string, MultiSelectDropdownItem[]> => {
  return new Map((groups ?? []).map((group) => [group.label, group.options]))
}

export const resolveCategoryOptionsForSelectAll = (
  categoryLabel: string,
  visibleCategoryOptions: MultiSelectDropdownItem[],
  fullCategoryOptionsByLabel?: ReadonlyMap<string, MultiSelectDropdownItem[]>
): MultiSelectDropdownItem[] => {
  return fullCategoryOptionsByLabel?.get(categoryLabel) ?? visibleCategoryOptions
}

export const getCategoryHeaderControlValue = (
  categoryOptions: MultiSelectDropdownItem[],
  value: Array<string | MultiSelectDropdownItem>,
  isOptionEqualToValue: (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem) => boolean
): CategoryHeaderControlValue => {
  const categoryLeaves = getLeafOptions(categoryOptions)
  const selectable = categoryLeaves.filter((option) => option.disabled !== true)
  if (selectable.length === 0) {
    return 'unchecked'
  }
  const selectedCount = selectable.filter((option) =>
    value.some((selectedValue) => isOptionEqualToValue(option, selectedValue))
  ).length
  if (selectedCount === 0) {
    return 'unchecked'
  }
  if (selectedCount === selectable.length) {
    return 'checked'
  }
  return 'indeterminate'
}

export const getSelectAllHeaderControlValue = (
  enabledOptions: MultiSelectDropdownItem[],
  value: Array<string | MultiSelectDropdownItem>,
  indeterminateValues: ReadonlySet<string>
): CategoryHeaderControlValue => {
  const hasIndeterminateSelection = enabledOptions.some((option) => indeterminateValues.has(option.value))
  if (value.length === 0) {
    return 'unchecked'
  }
  if (value.length === enabledOptions.length && !hasIndeterminateSelection) {
    return 'checked'
  }
  return 'indeterminate'
}

export const toggleCategorySelection = ({
  categoryOptions,
  leafOptions,
  value,
  isOptionEqualToValue,
  onChange
}: {
  categoryOptions: MultiSelectDropdownItem[]
  leafOptions: MultiSelectDropdownItem[]
  value: Array<string | MultiSelectDropdownItem>
  isOptionEqualToValue: (option: MultiSelectDropdownItem, value: string | MultiSelectDropdownItem) => boolean
  onChange: (optionValue: string | null, value: MultiSelectDropdownItem[]) => void
}): void => {
  const categoryLeaves = getLeafOptions(categoryOptions)
  const controlValue = getCategoryHeaderControlValue(categoryOptions, value, isOptionEqualToValue)

  const selectionsOutsideCategory = leafOptions.filter(
    (option) =>
      value.some((selectedValue) => isOptionEqualToValue(option, selectedValue)) &&
      !categoryLeaves.some((categoryOption) => isOptionEqualToValue(categoryOption, option))
  )

  if (controlValue === 'checked') {
    const lockedInCategory = categoryLeaves.filter(
      (option) => option.disabled === true && value.some((selectedValue) => isOptionEqualToValue(option, selectedValue))
    )
    onChange(null, [...selectionsOutsideCategory, ...lockedInCategory])
    return
  }

  const toSelectInCategory = categoryLeaves.filter(
    (option) => option.disabled !== true || value.some((selectedValue) => isOptionEqualToValue(option, selectedValue))
  )
  onChange(null, [...selectionsOutsideCategory, ...toSelectInCategory])
}
